import type { BatchedDeclarationRequest, PreviewedConsumption } from '@api/vendor-portal';
import { formatCount } from '@utils/currency';

export interface BatchGridRow {
    id: string;
    batchCode: string;
    /**
     * What this row is against, as the person chose it: '' for stock, `po:<id>` for a
     * production order, `wo:<id>` for a work order raised without one.
     *
     * THE PRODUCTION ORDER IS WHAT A FLOOR KNOWS. A site is told "make PO-DRIFIT-AUG"; the
     * per-SKU work order underneath is the brand's bookkeeping. Asking for the work order
     * asked people to translate, so this holds the choice and the work order is derived.
     */
    orderChoice: string;
    manufacturingDate: string;
    expiryDate: string;
    productId: string;
    quantity: string;
}

export const blankGridRow = (): BatchGridRow => ({
    id: Math.random().toString(36).slice(2),
    batchCode: '',
    orderChoice: '',
    manufacturingDate: '',
    expiryDate: '',
    productId: '',
    quantity: '',
});

/**
 * The row to add below the one just typed.
 *
 * A SHEET REPEATS ITSELF DOWNWARD. The next line of a shift sheet is usually another SKU of the
 * run above it, so the batch header, the dates and the order it answers all carry down and only
 * the SKU and the quantity are actually typed.
 *
 * THE ORDER CARRIES TOO, which it did not before: a row added to a grid seeded from an order
 * defaulted to "To stock", so what was typed on it was recorded but the order it was added to
 * never counted it. Everything carried is a default the row can be changed off.
 */
export function nextGridRow(last?: BatchGridRow): BatchGridRow {
    return {
        ...blankGridRow(),
        batchCode: last?.batchCode ?? '',
        orderChoice: last?.orderChoice ?? '',
        manufacturingDate: last?.manufacturingDate ?? '',
        expiryDate: last?.expiryDate ?? '',
    };
}

/**
 * The batch code from the first row that has one, put on every row that has none.
 *
 * ONE RUN PACKED INTO SEVERAL SKUS IS THE ORDINARY CASE - it is what `groupRowsIntoBatches`
 * below is built around - and on a seeded order grid it means typing the same code once per SKU.
 * This fills the blanks from the first code entered.
 *
 * IT ONLY EVER FILLS EMPTY ROWS. A row already carrying a code is a second run the person
 * deliberately named, and overwriting it would merge two batches into one on their behalf.
 */
export function fillDownBatchCode(rows: BatchGridRow[]): BatchGridRow[] {
    const source = rows.find((row) => row.batchCode.trim())?.batchCode.trim();
    if (!source) return rows;

    return rows.map((row) =>
        row.batchCode.trim() ? row : { ...row, batchCode: source },
    );
}

/**
 * Rows sharing a code become one batch.
 *
 * A code IS the batch — the same string typed on two rows names one run packed into two SKUs,
 * not two runs. Grouping here rather than asking the person to say so keeps the grid flat and
 * matches how the data arrives: a shift sheet lists what came off, and the same code appearing
 * twice is ordinary.
 *
 * THE FIRST ROW OF A CODE OWNS ITS DATES AND VERDICT. Later rows carrying different dates are a
 * contradiction the grid should surface rather than silently resolve, which `findConflicts`
 * below does — but the group still has to produce one header, and the first occurrence is the
 * one the person typed first.
 */
export interface OrderLine {
    workOrderId: string;
    /** The work order's own code, which is what the ledger will show. */
    code: string;
    productId: string;
    productionOrderId: string | null;
    productionOrderCode: string | null;
    /** When the order was placed - what FIFO sorts by. Null on a headerless run. */
    orderDate?: string | null;
    /** Ordered and made-so-far (shelf cover included), so a default can skip covered lines. */
    ordered?: number;
    produced?: number;
    /**
     * Declared against this line, shelf cover EXCLUDED - what the over-attribution cap
     * compares against. The folded `produced` is right for the FIFO default (skip covered
     * lines) and wrong here: a cap that counted virtual cover would tell a site "only 6,000
     * left" on an order the paper says is 10,000 with nothing physically made.
     */
    declared?: number;
}

/**
 * The site's open orders, flattened to the one line per work order that both pickers want.
 *
 * `SiteOrder` nests lines under their production order, which is right for a job list and wrong
 * for a dropdown — the same mapping was being written out at each call site, and a picker built
 * from a slightly different flattening would offer slightly different orders on two screens that
 * are meant to be looking at the same list.
 *
 * A work order raised with no header carries no production order, and says so with nulls rather
 * than borrowing its own code — `buildOrderOptions` reads that to decide where it belongs.
 */
export function toOrderLines(
    orders: Array<{
        productionOrderId: string | null;
        code: string;
        orderDate?: string | null;
        lines?: Array<{
            workOrderId: string;
            code: string;
            productId: string;
            ordered?: number;
            produced?: number;
            allocatedFromStock?: { total: number } | null;
        }>;
    }>,
): OrderLine[] {
    return orders.flatMap((order) =>
        (order.lines ?? []).map((line) => ({
            workOrderId: line.workOrderId,
            code: line.code,
            productId: line.productId,
            productionOrderId: order.productionOrderId,
            productionOrderCode: order.productionOrderId ? order.code : null,
            orderDate: order.orderDate ?? null,
            ordered: line.ordered,
            produced: line.produced,
            declared:
                line.produced === undefined
                    ? undefined
                    : line.produced - (line.allocatedFromStock?.total ?? 0),
        })),
    );
}

/**
 * The Against box's default for a freshly picked product: the earliest open order still
 * asking for it.
 *
 * FIFO BY ORDER DATE, skipping lines already covered - `produced` folds in the shelf-stock
 * allocation, so an order whose 10,000 are made (or covered) but unshipped is not offered a
 * second run. Nothing still needing the product returns '' (stock): anything beyond need IS
 * overrun, and saying so by default is the honest reading. The person can always override.
 */
export function defaultOrderChoiceForProduct(
    productId: string,
    lines: OrderLine[],
): string {
    const byOrderDateThenCode = (a: OrderLine, b: OrderLine) => {
        const aDate = a.orderDate ?? null;
        const bDate = b.orderDate ?? null;
        if (aDate && bDate && aDate !== bDate) return aDate < bDate ? -1 : 1;
        if (aDate && !bDate) return -1;
        if (!aDate && bDate) return 1;
        return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
    };

    const forProduct = lines.filter((line) => line.productId === productId);
    const open = forProduct
        .filter((line) => Math.max(0, (line.ordered ?? 0) - (line.produced ?? 0)) > 0)
        .sort(byOrderDateThenCode);

    /*
     * A product whose every order is already covered still defaults to its earliest order
     * rather than to nothing: with stock runs gone from the manufacturer's grid, an empty
     * choice would be a dead end, while an over-covered order lets the over-attribution
     * check say the real problem — reduce, or ask the brand for a new order.
     */
    const [first] = open.length > 0 ? open : forProduct.sort(byOrderDateThenCode);
    if (!first) return '';
    return first.productionOrderId ? `po:${first.productionOrderId}` : `wo:${first.workOrderId}`;
}

/**
 * What a row can be recorded or shipped against.
 *
 * OFFERED AS PRODUCTION ORDERS, RESOLVED TO WORK ORDERS. A person thinks in the order they were
 * given — "this is against PO-DRIFIT-AUG" — not in the per-SKU work orders it was exploded into,
 * and listing every work order separately would show the same order once per SKU on it. So the
 * production order is what appears, and `resolveWorkOrder` turns the choice plus the row's own
 * SKU back into exactly one line.
 *
 * Work orders raised without a header have nothing to be grouped under, so they appear by their
 * own code.
 *
 * THE STOCK ENTRY IS OPT-IN, named by the caller or absent. The shipment builder keeps it —
 * free batches already exist and still have to physically ship — and the brand's on-behalf
 * grid keeps it as the escape hatch. The manufacturer's own declare grid does not offer it:
 * a run answers an order, and over-production is a new production order from the brand, not a
 * silent pile.
 *
 * Shared by the declaration grid and the shipment builder, which ask the same question — which
 * order does this line belong to — of the same list.
 */
export function buildOrderOptions(
    lines: OrderLine[],
    stockLabel?: string,
): Array<{ value: string; label: string }> {
    const productionOrders = new Map<string, string>();
    const standalone: Array<{ value: string; label: string }> = [];

    for (const line of lines) {
        if (line.productionOrderId && line.productionOrderCode) {
            productionOrders.set(line.productionOrderId, line.productionOrderCode);
        } else {
            standalone.push({ value: `wo:${line.workOrderId}`, label: line.code });
        }
    }

    return [
        ...(stockLabel ? [{ value: '', label: stockLabel }] : []),
        ...[...productionOrders].map(([id, code]) => ({ value: `po:${id}`, label: code })),
        ...standalone,
    ];
}

/**
 * The SKUs an order actually asked for, as the product picker's options.
 *
 * BUILT FROM THE ORDER, NAMED FROM THE CATALOG. Intersecting the site's catalog with the order
 * gives the same answer today and would fail silently the day it stops: a line whose product the
 * catalog had dropped would disappear out of its own order's picker with nothing said. Taking the
 * lines as the source makes that impossible - the catalog only supplies names, and a product it
 * has no name for is offered by its id rather than left out.
 *
 * Deduped because a line list holds one entry per work order, so a SKU on three orders would
 * otherwise be offered three times.
 */
export function productOptionsForOrder(
    lines: OrderLine[],
    products: Array<{ itemId: string; name?: string | null }>,
): Array<{ value: string; label: string }> {
    const nameOf = new Map(products.map((product) => [product.itemId, product.name]));
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];

    for (const line of lines) {
        if (!line.productId || seen.has(line.productId)) continue;
        seen.add(line.productId);
        options.push({
            value: line.productId,
            label: nameOf.get(line.productId) || line.productId,
        });
    }

    return options;
}

/**
 * The work order a row lands on, given what the person picked and what they made.
 *
 * SAFE BY CONSTRUCTION, not by luck. createProductionOrder rejects a SKU appearing twice on
 * one order, and createWorkOrder supersedes an open work order for the same product within the
 * same order — so (production order, SKU) names exactly one live work order and there is
 * nothing to disambiguate.
 *
 * Null where the pair matches nothing, which is a typo worth showing rather than a run to
 * quietly record against stock: somebody who named an order meant to answer it.
 */
export function resolveWorkOrder(
    orderChoice: string,
    productId: string,
    lines: OrderLine[],
): { workOrderId: string | null; code: string | null; unmatched: boolean } {
    if (!orderChoice) return { workOrderId: null, code: null, unmatched: false };

    if (orderChoice.startsWith('wo:')) {
        const workOrderId = orderChoice.slice(3);
        const line = lines.find((l) => l.workOrderId === workOrderId);
        // A standalone work order names its own SKU, so a mismatch is still a mismatch.
        if (!line || (productId && line.productId !== productId)) {
            return { workOrderId: null, code: null, unmatched: Boolean(productId) };
        }
        return { workOrderId: line.workOrderId, code: line.code, unmatched: false };
    }

    const productionOrderId = orderChoice.slice(3);
    if (!productId) return { workOrderId: null, code: null, unmatched: false };
    const line = lines.find(
        (l) => l.productionOrderId === productionOrderId && l.productId === productId,
    );
    if (!line) return { workOrderId: null, code: null, unmatched: true };
    return { workOrderId: line.workOrderId, code: line.code, unmatched: false };
}

/**
 * Which declaration something belongs to: its batch code AND the order it answers.
 *
 * ONE FORMAT, written once, because three separate things have to agree on it. The submission
 * groups rows by it, the conflict warning is scoped by it, and the partial-write recovery
 * matches rows back to the declarations that already landed by it. Three copies of the same
 * template string, one of which quietly stops matching the other two, is how a recovery drops
 * the wrong rows off somebody's grid.
 */
const declarationKeyOf = (code: string, workOrderId?: string | null): string =>
    `${code}::${workOrderId ?? ''}`;

/**
 * The key for a grid row. A row with no code is not going anywhere, and gets an empty key that
 * no real declaration can collide with.
 */
export function declarationKey(row: BatchGridRow, lines: OrderLine[] = []): string {
    const code = row.batchCode.trim();
    if (!code) return '';
    const { workOrderId } = resolveWorkOrder(row.orderChoice, row.productId, lines);
    return declarationKeyOf(code, workOrderId);
}

/** The key for a built declaration, so a caller holding one can match it back to its rows. */
export function requestKey(request: BatchedDeclarationRequest): string {
    return declarationKeyOf(request.batch.code, request.workOrderId);
}

/**
 * Always batched: the grid is the batch-first way of declaring, and a row without a code is
 * skipped below rather than sent. The no-batch route lives in the single-run form, where the
 * brand's switch decides.
 */
export function groupRowsIntoBatches(
    rows: BatchGridRow[],
    lines: OrderLine[] = [],
): BatchedDeclarationRequest[] {
    const order: string[] = [];
    const byKey = new Map<string, BatchedDeclarationRequest>();

    for (const row of rows) {
        const code = row.batchCode.trim();
        const quantity = Number(row.quantity);
        if (!code || !row.productId || !Number.isFinite(quantity) || quantity <= 0) continue;

        const { workOrderId } = resolveWorkOrder(row.orderChoice, row.productId, lines);

        // Code AND order: the declaration carries one work order, so rows of one code
        // answering different orders are different declarations.
        const key = declarationKeyOf(code, workOrderId);
        let request = byKey.get(key);
        if (!request) {
            request = {
                batch: {
                    code,
                    manufacturingDate: row.manufacturingDate || undefined,
                    expiryDate: row.expiryDate || undefined,
                },
                outputs: [],
                ...(workOrderId ? { workOrderId } : {}),
            };
            byKey.set(key, request);
            order.push(key);
        }

        // Same SKU twice under one code is one line, not two — the server would treat them as
        // two declarations against the same batch and the ledger would read as two runs.
        const existing = request.outputs.find((o) => o.productId === row.productId);
        if (existing) existing.quantity += quantity;
        else request.outputs.push({ productId: row.productId, quantity });
    }

    return order.map((key) => byKey.get(key)!);
}

export interface GridConflict {
    batchCode: string;
    field: 'manufacturingDate' | 'expiryDate';
}

/**
 * Where one code has been given two different answers.
 *
 * Not blocked, but worth saying out loud: the first row wins, and somebody who typed a second
 * expiry meant something by it. Silently keeping one of two dates is how a batch ends up with
 * an expiry nobody chose.
 */
export interface DeclarationCheck {
    /** Row ids marked "Over by N" - the screen anchors the refusal to what caused it. */
    overByRow: Map<string, number>;
    /** One sentence per over-filled order, naming the way out - not just the refusal. */
    overfilled: string[];
}

/**
 * No submission may put more against an order line than it has left to fill.
 *
 * OVER-RUNNING STAYS LEGAL; OVER-ATTRIBUTION DOES NOT. A 10,000-unit order yielding 12,000
 * is ordinary manufacturing - the grid's own model holds the split (two rows, one batch
 * code: 10,000 against the order, 2,000 to stock). What this blocks is Manufactured
 * exceeding Ordered on the books, which corrupts the figure whoever keys it in.
 *
 * Rows against one line SUM before comparing - two rows both answering the same order are
 * one attribution. Mirrors checkShipment on the dispatch side, against `declared` (pegged
 * only - virtual shelf cover deliberately does not consume the cap).
 */
export function checkDeclarations(
    rows: BatchGridRow[],
    lines: OrderLine[] = [],
    allowStock = false,
): DeclarationCheck {
    const perLine = new Map<string, BatchGridRow[]>();

    for (const row of rows) {
        if (!row.productId) continue;
        const { workOrderId } = resolveWorkOrder(row.orderChoice, row.productId, lines);
        if (!workOrderId) continue;
        perLine.set(workOrderId, [...(perLine.get(workOrderId) ?? []), row]);
    }

    const overByRow = new Map<string, number>();
    const overfilled: string[] = [];

    for (const [workOrderId, lineRows] of perLine) {
        const line = lines.find((l) => l.workOrderId === workOrderId);
        if (!line || line.ordered === undefined) continue;

        const left = Math.max(0, line.ordered - (line.declared ?? 0));
        const total = lineRows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
        if (total <= left) continue;

        const code = line.productionOrderCode ?? line.code;
        // The way out differs by grid: the brand may split the excess to stock; the site has
        // no stock destination, so its only honest move is recording less.
        overfilled.push(
            `${code} has ${formatCount(left)} left to fill, and ${formatCount(total)} is going against it. ${
                allowStock
                    ? `Record ${formatCount(left)} against it and split the rest to stock.`
                    : `Record at most ${formatCount(left)} against it.`
            }`,
        );

        for (const row of lineRows) {
            overByRow.set(row.id, total - left);
        }
    }

    return { overByRow, overfilled };
}

export function findConflicts(rows: BatchGridRow[], lines: OrderLine[] = []): GridConflict[] {
    const seen = new Map<string, BatchGridRow>();
    const conflicts: GridConflict[] = [];

    for (const row of rows) {
        const code = row.batchCode.trim();
        if (!code) continue;

        /*
         * KEYED THE WAY THE SUBMISSION GROUPS, code AND order, not code alone.
         *
         * It used to key on the code, and warned that the first row's dates would win whenever
         * one code carried two. That is only true of rows heading into the SAME declaration —
         * and since a work order names one product, three SKUs under one code are three work
         * orders, three declarations, and three dates all of which are kept.
         *
         * It fired on a real grid of three SKUs whose dates were every one of them recorded,
         * telling somebody their data had been dropped when it had not. A warning that cries
         * wolf on correct input is worse than none: the next one, on the case that IS lossy —
         * two SKUs under one code, both to stock, sharing one declaration — gets ignored too.
         */
        const key = declarationKey(row, lines);

        const first = seen.get(key);
        if (!first) {
            seen.set(key, row);
            continue;
        }
        for (const field of ['manufacturingDate', 'expiryDate'] as const) {
            // Blank means "not stated here", not "different" — only a stated difference counts.
            if (row[field] && first[field] && row[field] !== first[field]) {
                // Reported by code, because that is what the person sees on the row, even though
                // it is detected per declaration.
                if (!conflicts.some((c) => c.batchCode === code && c.field === field)) {
                    conflicts.push({ batchCode: code, field });
                }
            }
        }
    }

    return conflicts;
}

export interface CumulativeDraw {
    materialId: string;
    materialName: string;
    required: number;
    available: number;
    short: number;
}

/**
 * What every batch together will draw, against what is actually on the shelf.
 *
 * THE WHOLE REASON THIS IS NOT PER-BATCH. Each preview is computed against current stock, so
 * three batches previewed independently each see the full pool and all three look feasible —
 * then the third fails on submit. Summing the requirements and comparing once is what makes a
 * shortfall visible while it can still be fixed.
 */
export function buildCumulativeDraw(
    previews: PreviewedConsumption[][],
    availableByMaterial: Map<string, { name: string; usable: number }>,
): CumulativeDraw[] {
    const required = new Map<string, { name: string; required: number }>();

    for (const preview of previews) {
        for (const line of preview) {
            const entry = required.get(line.materialId) ?? {
                name: line.materialName ?? line.materialId,
                required: 0,
            };
            entry.required += line.required;
            required.set(line.materialId, entry);
        }
    }

    return [...required.entries()]
        .map(([materialId, entry]) => {
            const stock = availableByMaterial.get(materialId);
            const available = stock?.usable ?? 0;
            return {
                materialId,
                materialName: stock?.name ?? entry.name,
                required: entry.required,
                available,
                short: Math.max(0, entry.required - available),
            };
        })
        .sort((a, b) => b.short - a.short || a.materialName.localeCompare(b.materialName));
}

/** Header names accepted from a CSV, lowercased and stripped of spaces and punctuation. */
const CSV_ALIASES: Record<string, keyof BatchGridRow> = {
    batchcode: 'batchCode',
    batch: 'batchCode',
    code: 'batchCode',
    lotnumber: 'batchCode',
    batchnumber: 'batchCode',
    manufactured: 'manufacturingDate',
    manufacturingdate: 'manufacturingDate',
    mfgdate: 'manufacturingDate',
    dateofmanufacturing: 'manufacturingDate',
    expires: 'expiryDate',
    expiry: 'expiryDate',
    expirydate: 'expiryDate',
    product: 'productId',
    productid: 'productId',
    sku: 'productId',
    quantity: 'quantity',
    qty: 'quantity',
    // Matched by CODE, then resolved to a choice by the caller — the file names the order the
    // way the brand does, and the ids mean nothing to whoever typed the sheet.
    productionorder: 'orderChoice',
    order: 'orderChoice',
    po: 'orderChoice',
    ordercode: 'orderChoice',
};

const normaliseHeader = (header: string) => header.toLowerCase().replace(/[^a-z]/g, '');

export interface CsvParseResult {
    rows: BatchGridRow[];
    /** Headers the file carried that mean nothing here — named so a typo is findable. */
    unknownHeaders: string[];
    /** Product names or codes that matched no SKU, so the row landed with no product. */
    unmatchedProducts: string[];
    /**
     * Data rows (1-based, header excluded) whose Production order cell is blank. The column
     * is mandatory - a run answers an order, or says "Stock" out loud. Silence rejects the
     * file, because a blank that quietly meant stock is how misattribution becomes habit.
     */
    missingOrder: number[];
    /** Order cells that matched no open order - a typo must not quietly become stock. */
    unknownOrders: string[];
    /**
     * Data rows that wrote "Stock" where stock runs are not accepted. The manufacturer's grid
     * refuses them - a run answers an order, and over-production is a new production order
     * from the brand - while the brand's on-behalf grid still allows the word.
     */
    stockRuns: number[];
}

/**
 * A shift sheet, as exported from whatever the site actually keeps it in.
 *
 * MATCHES ON WHAT PEOPLE TYPE, not on ids. A site's sheet names a product the way the floor
 * says it, so the product column is matched against name first and id second, both
 * case-insensitively. A row whose product cannot be matched still lands — with the cell empty
 * and the name reported back — because dropping it silently is how half a sheet goes missing.
 *
 * Deliberately forgiving about headers and dates, and deliberately not forgiving about
 * quantities: a quantity that will not parse is left as typed so somebody looks at it, rather
 * than being read as zero.
 */
export function parseProductionCsv(
    text: string,
    products: Array<{ itemId: string; name?: string | null }>,
    lines: OrderLine[] = [],
    allowStock = false,
): CsvParseResult {
    const textLines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (textLines.length < 2) {
        return {
            rows: [],
            unknownHeaders: [],
            unmatchedProducts: [],
            missingOrder: [],
            unknownOrders: [],
            stockRuns: [],
        };
    }

    const split = (line: string) =>
        line.split(',').map((cell) => cell.trim().replace(/^"(.*)"$/, '$1'));

    const headers = split(textLines[0]);
    const unknownHeaders: string[] = [];
    const fields = headers.map((header) => {
        const field = CSV_ALIASES[normaliseHeader(header)];
        if (!field) unknownHeaders.push(header);
        return field;
    });

    const byName = new Map<string, string>();
    for (const product of products) {
        if (product.name) byName.set(product.name.toLowerCase(), product.itemId);
        byName.set(product.itemId.toLowerCase(), product.itemId);
    }

    // Order codes as the sheet writes them: a production order code, or a work order's own
    // code where the run was raised without a header.
    const byOrderCode = new Map<string, string>();
    for (const line of lines) {
        if (line.productionOrderId && line.productionOrderCode) {
            byOrderCode.set(line.productionOrderCode.toLowerCase(), `po:${line.productionOrderId}`);
        }
        byOrderCode.set(line.code.toLowerCase(), `wo:${line.workOrderId}`);
    }

    const unmatchedProducts: string[] = [];
    const missingOrder: number[] = [];
    const unknownOrders: string[] = [];
    const stockRuns: number[] = [];
    const rows: BatchGridRow[] = [];

    textLines.slice(1).forEach((line, rowNumber) => {
        const cells = split(line);
        const row = blankGridRow();
        let touched = false;
        /*
         * MANDATORY, AND EXPLICIT. A run answers an order, or the sheet writes "Stock" and
         * owns the choice. Neither a blank cell nor an unrecognised code may quietly become
         * a stock run - the first is silence, the second is a typo wearing silence's clothes.
         */
        let orderStated = false;

        fields.forEach((field, index) => {
            const value = cells[index];
            if (!field || value === undefined || value === '') return;
            touched = true;

            if (field === 'productId') {
                const matched = byName.get(value.toLowerCase());
                if (matched) row.productId = matched;
                else if (!unmatchedProducts.includes(value)) unmatchedProducts.push(value);
            } else if (field === 'batchCode') {
                row.batchCode = value;
            } else if (field === 'manufacturingDate') {
                row.manufacturingDate = value;
            } else if (field === 'expiryDate') {
                row.expiryDate = value;
            } else if (field === 'quantity') {
                row.quantity = value;
            } else if (field === 'orderChoice') {
                if (value.trim().toLowerCase() === 'stock') {
                    if (allowStock) {
                        row.orderChoice = '';
                        orderStated = true;
                    } else {
                        // Stated, but not accepted: the row named stock where stock runs are
                        // refused, which is its own message rather than "blank".
                        orderStated = true;
                        stockRuns.push(rowNumber + 1);
                    }
                } else {
                    const matched = byOrderCode.get(value.toLowerCase());
                    if (matched) {
                        row.orderChoice = matched;
                        orderStated = true;
                    } else if (!unknownOrders.includes(value)) {
                        unknownOrders.push(value);
                    }
                }
            }
        });

        if (!touched) return;
        rows.push(row);
        if (!orderStated) missingOrder.push(rowNumber + 1);
    });

    return { rows, unknownHeaders, unmatchedProducts, missingOrder, unknownOrders, stockRuns };
}

/**
 * The header row this grid writes and reads back without translation.
 *
 * IN THE GRID'S OWN COLUMN ORDER, product first — a template that disagrees with the screen it
 * fills is a small trap for anyone reading across from one to the other. Only the template
 * cares about this order: an upload matches on the header NAME, so a sheet saved from an older
 * template, or keyed in any order at all, goes on importing exactly as before.
 *
 * `productionTemplateCsv` builds its sample rows positionally against this list. The two move
 * together or every sample value lands one column off its heading.
 */
export const PRODUCTION_CSV_HEADERS = [
    'Product',
    'Batch code',
    'Quantity',
    'Manufactured',
    'Production order',
    'Expires',
] as const;

/**
 * A template filled in with this site's own products and orders.
 *
 * SHOWS RATHER THAN TELLS. A header row alone leaves somebody guessing what goes under
 * "Production order" — a code? an id? — so the sample rows carry real values read from what
 * this site actually has open. Two rows share a batch code on purpose: that is the rule people
 * most often get wrong, and one example of it is worth a paragraph of instruction.
 *
 * Falls back to plain placeholders for a site with nothing open yet, which is the one case
 * where there is nothing real to show.
 */
export function productionTemplateCsv(
    products: Array<{ itemId: string; name?: string | null }>,
    lines: OrderLine[],
): string {
    const nameOf = (itemId: string) =>
        products.find((p) => p.itemId === itemId)?.name ?? itemId;

    /*
     * EVERY SAMPLE NAMES AN ORDER. Stock runs are no longer recorded from the manufacturer's
     * grid, so a template teaching the word "Stock" would teach a rejection. The samples use
     * products genuinely ON the orders they name — a template whose own rows would come back
     * flagged teaches the wrong thing, and the round-trip test exists because the first
     * version of this did exactly that.
     */
    const orderLine = lines.find((line) => line.productionOrderCode) ?? lines[0] ?? null;
    const orderCode = orderLine
        ? (orderLine.productionOrderCode ?? orderLine.code)
        : 'PO-CODE';
    const orderProduct = orderLine
        ? nameOf(orderLine.productId)
        : (products[0]?.name ?? products[0]?.itemId ?? 'Product name');

    // A second SKU from any open line, writing ITS OWN order code — rows sharing a batch code
    // may answer different orders, and the sample must only ever name pairings that exist. A
    // site with one open SKU repeats it: the shared-code rule is still shown, and a repeated
    // SKU merges into one line on import rather than coming back flagged.
    const secondLine =
        lines.find((line) => line.productId !== orderLine?.productId) ?? null;
    const secondProduct = secondLine ? nameOf(secondLine.productId) : orderProduct;
    const secondCode = secondLine
        ? (secondLine.productionOrderCode ?? secondLine.code)
        : orderCode;

    const rows = [
        // Two rows, one code: a single run packed into two SKUs, each answering its order —
        // sharing a batch code is the rule people most often get wrong.
        [orderProduct, 'RUN-0801', '400', '2026-08-01', orderCode, '2029-08-01'],
        [secondProduct, 'RUN-0801', '120', '2026-08-01', secondCode, '2029-08-01'],
        [orderProduct, 'RUN-0802', '250', '2026-08-02', orderCode, ''],
    ];

    const escape = (cell: string) => (cell.includes(',') ? `"${cell}"` : cell);
    return [PRODUCTION_CSV_HEADERS.join(','), ...rows.map((r) => r.map(escape).join(','))].join(
        '\n',
    );
}
